import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './BookingComponents.css';
import { logger } from '@core/logger';
import { IconLock } from '../icons/HeaderUiIcons';
import { stripEmojiUnicode } from '../../utils/stripEmoji';

export interface IdentityField {
  id: string;
  type: 'email' | 'phone' | 'name' | 'text';
  label: string;
  required: boolean;
  validation?: string;
}

export interface IdentityOption {
  id: string;
  type: 'login' | 'guest';
  label: string;
  description?: string;
  /** Si vienen, sustituyen los textos por defecto de la tarjeta */
  cardTitle?: string;
  cardDescription?: string;
  /** Texto bajo el título del formulario invitado */
  guestFormHint?: string;
  allowGoogleLogin?: boolean;
  allowClerkLogin?: boolean;
  identityFields?: IdentityField[];
}

interface IdentityFormProps {
  fields?: IdentityField[];
  allowGoogleLogin?: boolean;
  allowClerkLogin?: boolean;
  message?: string;
  identityOptions?: IdentityOption[];
  /** Título del paso con tarjetas login / invitado (default: booking.confirm_booking_title) */
  optionsHeading?: string;
  /** Título del formulario invitado (default: booking.contact_info_title) */
  guestFormHeading?: string;
  /** Etiqueta del botón enviar invitado (default: booking.confirm_booking) */
  guestSubmitLabel?: string;
  onSubmit: (data: Record<string, string>) => void;
  onLogin?: () => void;
}

export function IdentityForm({
  fields,
  allowGoogleLogin,
  allowClerkLogin,
  message,
  identityOptions,
  optionsHeading,
  guestFormHeading,
  guestSubmitLabel,
  onSubmit,
  onLogin,
}: IdentityFormProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [selectedOption, setSelectedOption] = useState<string | null>(null);

  const handleChange = (id: string, value: string) => {
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleOptionSelect = (option: IdentityOption) => {
    setSelectedOption(option.id);
    
    if (option.type === 'login') {
      // ✅ Llamar a Clerk para login/registro
      if (onLogin) {
        onLogin();
      } else {
        // Fallback: redirigir a Clerk sign-in
        const clerkSignInUrl = `${window.location.origin}/sign-in?redirect_url=${encodeURIComponent(window.location.href)}`;
        window.location.href = clerkSignInUrl;
      }
    } else if (option.type === 'guest' && option.identityFields) {
      // Continuar como invitado: mostrar formulario con campos
      setFormData({});
    }
  };

  const handleClerkLogin = () => {
    // ✅ Redirigir a Clerk sign-in
    const currentUrl = window.location.href;
    const clerkSignInUrl = `${window.location.origin}/sign-in?redirect_url=${encodeURIComponent(currentUrl)}`;
    window.location.href = clerkSignInUrl;
  };

  const handleGoogleLogin = () => {
    const currentUrl = window.location.href;
    const clerkSignInUrl = `${window.location.origin}/sign-in?redirect_url=${encodeURIComponent(currentUrl)}`;
    window.location.href = clerkSignInUrl;
  };

  const goClerkLogin = () => {
    if (onLogin) onLogin();
    else handleClerkLogin();
  };

  if (import.meta.env.DEV) {
    logger.debug('IdentityForm props', {
      hasIdentityOptions: !!identityOptions,
      identityOptionsLength: identityOptions?.length,
      identityOptions,
      allowClerkLogin,
      allowGoogleLogin,
      fields,
    });
  }

  const fieldLabel = (fieldId: string): string => {
    if (fieldId === 'email') return t('booking.email_label');
    if (fieldId === 'name') return t('booking.name_optional');
    if (fieldId === 'phone') return t('booking.phone_optional');
    return fieldId;
  };

  // ✅ Si hay identityOptions, mostrar opciones primero
  if (identityOptions && identityOptions.length > 0) {
    const selectedOptionData = identityOptions.find(opt => opt.id === selectedOption);
    
    // Si se seleccionó "guest" y tiene campos, mostrar formulario
    if (selectedOptionData && selectedOptionData.type === 'guest' && selectedOptionData.identityFields) {
      const guestHint = stripEmojiUnicode(
        selectedOptionData.guestFormHint ||
          selectedOptionData.description ||
          selectedOptionData.cardDescription ||
          t('booking.continue_guest_description'),
      );

      return (
        <div className="booking-step">
          <h2>{guestFormHeading ?? t('booking.contact_info_title')}</h2>

          <p className="identity-message">{guestHint}</p>

          <form className="identity-form" onSubmit={handleSubmit}>
            {selectedOptionData.identityFields.map((field) => (
              <div key={field.id} className="form-field">
                <label htmlFor={field.id}>
                  {fieldLabel(field.id)}
                  {field.required && <span className="required">*</span>}
                </label>
                <input
                  type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'}
                  id={field.id}
                  value={formData[field.id] || ''}
                  onChange={(e) => handleChange(field.id, e.target.value)}
                  required={field.required}
                />
              </div>
            ))}
            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={() => setSelectedOption(null)}>
                {t('booking.back')}
              </button>
              <button type="submit" className="btn-primary">
                {guestSubmitLabel ?? t('booking.confirm_booking')}
              </button>
            </div>
          </form>
        </div>
      );
    }

    if (import.meta.env.DEV) {
      logger.debug('IdentityForm identityOptions', identityOptions);
    }

    return (
      <div className="booking-step">
        <h2>{optionsHeading ?? t('booking.confirm_booking_title')}</h2>

        <p className="identity-message">
          {stripEmojiUnicode(message || t('booking.confirm_booking_intro'))}
        </p>

        <div className="identity-options">
          {identityOptions.map((option) => {
            const cardTitle = stripEmojiUnicode(
              option.cardTitle ??
                (option.id === 'login' ? t('booking.login_register') : t('booking.continue_guest')),
            );
            const cardDesc = stripEmojiUnicode(
              option.cardDescription ??
                (option.id === 'login'
                  ? t('booking.login_register_description')
                  : t('booking.continue_guest_description')),
            );

            return (
              <div key={option.id} className="identity-option-card">
                <h3>{cardTitle}</h3>
                <p>{cardDesc}</p>

                {option.type === 'login' && (
                  <div className="login-buttons">
                    {option.allowClerkLogin && (
                      <button type="button" className="btn-clerk" onClick={goClerkLogin}>
                        <IconLock className="identity-inline-icon" aria-hidden />
                        {t('booking.login_register')}
                      </button>
                    )}
                    {option.allowGoogleLogin && (
                      <button type="button" className="btn-google" onClick={handleGoogleLogin}>
                        <span className="google-icon">G</span>
                        {t('booking.continue_google')}
                      </button>
                    )}
                    {!option.allowClerkLogin && !option.allowGoogleLogin && (
                      <p className="text-muted">{t('booking.login_options_unavailable')}</p>
                    )}
                  </div>
                )}

                {option.type === 'guest' && (
                  <button type="button" className="btn-primary" onClick={() => handleOptionSelect(option)}>
                    {t('booking.continue_guest')}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ✅ Formato legacy: mostrar formulario directamente (compatibilidad)
  // ✅ Debug: Log para formato legacy
  if (import.meta.env.DEV) {
    logger.debug('IdentityForm legacy format', {
      hasFields: !!fields,
      fieldsLength: fields?.length,
      allowClerkLogin,
      allowGoogleLogin,
      message,
    });
  }

  return (
    <div className="booking-step">
      <h2>{t('booking.contact_info_title')}</h2>

      {message && <p className="identity-message">{stripEmojiUnicode(message)}</p>}

      {(allowGoogleLogin || allowClerkLogin) && (
        <div className="login-section">
          {allowClerkLogin && (
            <button type="button" className="btn-clerk" onClick={goClerkLogin}>
              <IconLock className="identity-inline-icon" aria-hidden />
              {t('booking.login_register')}
            </button>
          )}
          {allowGoogleLogin && (
            <button 
              type="button" 
              className="btn-google" 
              onClick={handleGoogleLogin}
            >
              <span className="google-icon">G</span>
              {t('booking.continue_google')}
            </button>
          )}
          {fields && fields.length > 0 && (
            <div className="divider">
              <span>o</span>
            </div>
          )}
        </div>
      )}

      {fields && fields.length > 0 && (
        <form className="identity-form" onSubmit={handleSubmit}>
          {fields.map((field) => (
            <div key={field.id} className="form-field">
              <label htmlFor={field.id}>
                {fieldLabel(field.id)}
                {field.required && <span className="required">*</span>}
              </label>
              <input
                type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'}
                id={field.id}
                value={formData[field.id] || ''}
                onChange={(e) => handleChange(field.id, e.target.value)}
                required={field.required}
              />
            </div>
          ))}
          <div className="form-actions">
            <button type="submit" className="btn-primary">
              {t('booking.continue_button')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
